import { SNCR_ANVISA_BASE_URL, SNCR_CALLBACK_ORIGIN } from '../../../shared/api';
import { SNCR_CALLBACK_PATH } from './constants';

interface GovBrLogin {
  state: string;
  loginUrl: string;
}

interface GovBrSession {
  state: string | null;
  sessionId: string;
}

const SESSION_STORAGE_KEY = 'sncr-govbr-session';
const SESSION_MESSAGE_TYPE = 'sncr-govbr-session';

interface GovBrSessionMessage extends GovBrSession {
  type: typeof SESSION_MESSAGE_TYPE;
}

function randomUuidFromCrypto(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

function generateState(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return randomUuidFromCrypto();
  }
  return `sncr-${Math.random().toString(36).slice(2)}${Date.now()}`;
}

export function buildGovBrLogin(): GovBrLogin {
  const state = generateState();
  window.localStorage.removeItem(SESSION_STORAGE_KEY);

  const callbackOrigin = SNCR_CALLBACK_ORIGIN || window.location.origin;
  const params = new URLSearchParams({
    client_url: `${callbackOrigin}${SNCR_CALLBACK_PATH}`,
    state,
  });
  const loginUrl = `${SNCR_ANVISA_BASE_URL}/auth/login?${params.toString()}`;

  return { state, loginUrl };
}

export function saveGovBrSession(state: string | null, sessionId: string): void {
  const session: GovBrSession = { state, sessionId };
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

/**
 * Envia o session id do Gov.br do popup de callback para a janela que o abriu.
 *
 * Quando a modal roda em iframe dentro de outro site (ex.: B2C), o navegador
 * particiona o `localStorage` do iframe e ele não enxerga o que o popup grava
 * ({@link saveGovBrSession}); o `postMessage` não sofre esse particionamento.
 *
 * @param state - `state` devolvido pelo Gov.br, para o opener validar a tentativa.
 * @param sessionId - Session id do Gov.br recebido no callback.
 */
export function postGovBrSessionToOpener(state: string | null, sessionId: string): void {
  const message: GovBrSessionMessage = { type: SESSION_MESSAGE_TYPE, state, sessionId };
  window.opener?.postMessage(message, window.location.origin);
}

/**
 * Extrai o session id de uma mensagem enviada por {@link postGovBrSessionToOpener}.
 *
 * @param data - `event.data` recebido no listener de `message`.
 * @param state - `state` da tentativa de login em andamento.
 * @returns O session id, ou `null` se a mensagem não for do callback ou for de outra tentativa.
 */
export function readGovBrSessionMessage(data: unknown, state: string): string | null {
  const message = data as Partial<GovBrSessionMessage> | null;
  if (message?.type !== SESSION_MESSAGE_TYPE || message.state !== state) return null;
  return message.sessionId || null;
}

export function takeGovBrSessionId(state: string): string | null {
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;

  window.localStorage.removeItem(SESSION_STORAGE_KEY);

  try {
    const session = JSON.parse(raw) as GovBrSession;
    if (session.state && session.state !== state) return null;
    return session.sessionId || null;
  } catch {
    return null;
  }
}
